import { useMemo, useState } from 'react';
import { Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import type { DependencyFacts, Ticket } from '../api';
import { chainThrough, layoutGraph, refsIn, selectEdges, waveGroups } from '../dependencyLayout';
import { DependencyGraph } from './DependencyGraph';
import { DependencyWaves } from './DependencyWaves';

/**
 * The "Order of work" panel: one set of links, three ways of reading it.
 *
 * They answer different questions and none of them replaces the others. **Graph** shows
 * the shape of the epic. **Waves** answers what is startable now and what each round
 * releases, with no lines to trace. **Focus** answers what stands behind one ticket —
 * across a real epic that neighbourhood is a handful of tickets, which is the size a
 * person actually reads.
 *
 * All three draw the same population: tickets some link touches. DWLF-191.
 */

type View = 'graph' | 'waves' | 'focus';

const VIEW_HINT: Record<View, string> = {
  graph: 'Blockers left to right. Click a ticket to open it.',
  waves: 'Grouped by how many rounds of unfinished work stand in front of them.',
  focus: 'One ticket with only its chain lit. Click any ticket to trace that one instead.',
};

interface Toggles {
  showRelatesTo: boolean;
  showImplied: boolean;
  showCriticalPath: boolean;
}

const TOGGLE_HINT: Record<keyof Toggles, string> = {
  showRelatesTo: 'Context links. They constrain no order, so they are off by default.',
  showImplied:
    'Links another path already implies. They add a line and no constraint, so they are off by default — turn them on to find one worth deleting.',
  showCriticalPath: 'The longest run of unfinished work, which is what sets how long the epic takes.',
};

function ViewControls({
  view,
  toggles,
  onChange,
}: {
  view: View;
  toggles: Toggles;
  onChange: (next: Partial<Toggles & { view: View }>) => void;
}) {
  const toggle = (key: keyof Toggles, label: string) => (
    <Chip
      size="small"
      label={label}
      variant={toggles[key] ? 'filled' : 'outlined'}
      onClick={() => onChange({ [key]: !toggles[key] })}
      title={TOGGLE_HINT[key]}
    />
  );

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Tabs
        value={view}
        onChange={(_, next: View) => onChange({ view: next })}
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
      >
        <Tab value="graph" label="Graph" />
        <Tab value="waves" label="Waves" />
        <Tab value="focus" label="Focus" />
      </Tabs>

      {view !== 'waves' && toggle('showRelatesTo', 'relates-to')}
      {view !== 'waves' && toggle('showImplied', 'Implied links')}
      {view === 'graph' && toggle('showCriticalPath', 'Critical path')}
    </Stack>
  );
}

function ViewCaption({
  view,
  subject,
  chainSize,
  longestChain,
}: {
  view: View;
  subject: number | null;
  chainSize: number;
  longestChain: number[];
}) {
  return (
    <Typography variant="caption" color="text.secondary">
      {VIEW_HINT[view]}
      {view === 'focus' && subject !== null && (
        <>
          {' '}
          <Box component="span" sx={{ color: 'text.primary' }}>
            DWLF-{subject}
          </Box>{' '}
          &mdash; {chainSize - 1} ticket(s) on its chain.
        </>
      )}
      {view === 'graph' && longestChain.length > 1 && (
        <> Longest chain: {longestChain.map((ref) => `DWLF-${ref}`).join(' \u2192 ')}.</>
      )}
    </Typography>
  );
}

/**
 * What the panel is leaving out, counted against what it is actually drawing.
 *
 * The toggles change the population and not merely the line count, so this has to be
 * measured from the drawn set — counting unlinked tickets against every recorded edge
 * would under-report by every ticket that only a hidden link reached.
 */
function NotDrawn({
  drawn,
  inEpic,
  external,
}: {
  drawn: number[];
  inEpic: Ticket[];
  external: number[];
}) {
  const shown = new Set(drawn);
  const missing = inEpic.filter((ticket) => !shown.has(ticket.ref)).length;
  const shownExternal = external.filter((ref) => shown.has(ref));

  return (
    <Box>
      {missing > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          {missing} ticket(s) in this epic are not drawn — nothing currently shown links them.
        </Typography>
      )}
      {shownExternal.length > 0 && (
        <Typography variant="caption" color="text.secondary" display="block">
          Dashed boxes are tickets in other epics:{' '}
          {shownExternal.map((ref) => `DWLF-${ref}`).join(', ')}.
        </Typography>
      )}
    </Box>
  );
}

/** Everything the three views need, derived once from the links and the toggles. */
function useDrawing(facts: DependencyFacts, toggles: Toggles) {
  const edges = useMemo(
    () =>
      selectEdges(facts.edges, {
        showRelatesTo: toggles.showRelatesTo,
        hideImpliedEdges: !toggles.showImplied,
        keepChain: facts.criticalPath,
      }),
    [facts.edges, facts.criticalPath, toggles.showRelatesTo, toggles.showImplied],
  );
  const drawn = useMemo(() => refsIn(edges), [edges]);

  return {
    edges,
    drawn,
    layout: useMemo(() => layoutGraph(edges), [edges]),
    waves: useMemo(() => waveGroups(drawn, facts.chainDepth), [drawn, facts.chainDepth]),
    external: useMemo(() => new Set(facts.external), [facts.external]),
  };
}

export function OrderOfWork({
  facts,
  tickets,
  inEpic,
  onOpen,
}: {
  facts: DependencyFacts;
  /** Every ticket on the board — a blocker is often in another epic. */
  tickets: Ticket[];
  /** This epic's own tickets, so the panel can say what it is leaving out. */
  inEpic: Ticket[];
  onOpen: (ref: number) => void;
}) {
  const [view, setView] = useState<View>('graph');
  const [toggles, setToggles] = useState<Toggles>({
    showRelatesTo: false,
    showImplied: false,
    showCriticalPath: true,
  });
  const [focused, setFocused] = useState<number | null>(null);

  const { edges, drawn, layout, waves, external } = useDrawing(facts, toggles);
  const subject = focused !== null && drawn.includes(focused) ? focused : (drawn[0] ?? null);
  const chain = useMemo(
    () => (subject === null ? new Set<number>() : chainThrough(edges, subject)),
    [edges, subject],
  );

  if (drawn.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No tickets in this epic are linked to anything yet. Link two with <code>blocks</code> and
        the order shows up here.
      </Typography>
    );
  }

  const emphasised =
    view === 'focus'
      ? new Set(subject === null ? [] : [subject])
      : new Set(toggles.showCriticalPath ? facts.criticalPath : []);
  const faded =
    view === 'focus' ? new Set(drawn.filter((ref) => !chain.has(ref))) : new Set<number>();

  return (
    <Stack spacing={1}>
      <ViewControls
        view={view}
        toggles={toggles}
        onChange={({ view: nextView, ...nextToggles }) => {
          if (nextView !== undefined) setView(nextView);
          if (Object.keys(nextToggles).length > 0) {
            setToggles((current) => ({ ...current, ...nextToggles }));
          }
        }}
      />

      <ViewCaption
        view={view}
        subject={subject}
        chainSize={chain.size}
        longestChain={toggles.showCriticalPath ? facts.criticalPath : []}
      />

      {view === 'waves' ? (
        <DependencyWaves waves={waves} tickets={tickets} external={external} onOpen={onOpen} />
      ) : (
        <DependencyGraph
          layout={layout}
          edges={edges}
          tickets={tickets}
          external={external}
          emphasised={emphasised}
          faded={faded}
          onTicketClick={view === 'focus' ? setFocused : onOpen}
        />
      )}

      {view === 'focus' && subject !== null && (
        <Box>
          <Chip size="small" label={`Open DWLF-${subject}`} onClick={() => onOpen(subject)} />
        </Box>
      )}

      <NotDrawn drawn={drawn} inEpic={inEpic} external={facts.external} />
    </Stack>
  );
}
